import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn.js';

export type MetricTone = 'ink' | 'brand' | 'ok' | 'warn' | 'info' | 'mute';

const TONE: Record<MetricTone, string> = {
  ink: 'text-ink',
  brand: 'text-brand-text',
  ok: 'text-ok',
  warn: 'text-warn',
  info: 'text-info',
  mute: 'text-mute',
};

export interface MetricRowProps {
  children: ReactNode;
  /** Column count. Default: one column per child (auto-fit). */
  columns?: 2 | 3 | 4;
  className?: string;
}

/** Inline metric wrapper with faked 1px gaps (DESIGN_SYSTEM section 8.7 / 5.4). */
export function MetricRow({ children, columns, className }: MetricRowProps) {
  return (
    <div
      className={cn(
        'grid gap-px overflow-hidden rounded-sm border border-hair bg-hair',
        columns === 2 ? 'grid-cols-2' : columns === 3 ? 'grid-cols-3' : columns === 4 ? 'grid-cols-4' : 'grid-flow-col auto-cols-fr',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface MetricCellProps {
  label: string;
  value: ReactNode;
  tone?: MetricTone;
  /** Small mono qualifier after the value (`/48`, `%`). */
  unit?: string;
  className?: string;
}

/** Inline cell: bg --panel px-3 py-3, mono label, text-xl value. Place inside MetricRow. */
export function MetricCell({ label, value, tone = 'ink', unit, className }: MetricCellProps) {
  return (
    <div className={cn('min-w-0 bg-panel px-3 py-3', className)}>
      <p className="label-mono truncate">{label}</p>
      <p className={cn('mt-1 font-mono text-xl tabular-nums leading-none tracking-tight', TONE[tone])}>
        {value}
        {unit ? <span className="ml-0.5 text-xs text-mute">{unit}</span> : null}
      </p>
    </div>
  );
}

export interface MetricStackedProps {
  label: string;
  value: ReactNode;
  /** Secondary line beneath the value: text-xs --mute. */
  sub?: ReactNode;
  tone?: MetricTone;
  /** Optional 14px leading icon on the label. */
  icon?: LucideIcon;
  /** Hero size (72px numeric-hero) instead of the 30px card numeral. */
  hero?: boolean;
  /** Content placed to the right of the value (delta, sparkline). */
  aside?: ReactNode;
  className?: string;
}

/** Stacked metric: mono label, numeric-hero value (text-3xl font-light tabular-nums), sub line. */
export function MetricStacked({ label, value, sub, tone = 'ink', icon: Icon, hero = false, aside, className }: MetricStackedProps) {
  return (
    <div className={cn('min-w-0', className)}>
      <p className="label-mono flex items-center gap-1.5">
        {Icon ? <Icon size={14} strokeWidth={1.5} aria-hidden className="text-mute" /> : null}
        {label}
      </p>
      <div className="mt-1 flex items-end gap-3">
        <p className={cn('numeric-hero font-mono leading-none', hero ? 'text-[72px]' : 'text-3xl font-light', TONE[tone])}>{value}</p>
        {aside ? <div className="pb-1">{aside}</div> : null}
      </div>
      {sub ? <p className="mt-1.5 font-mono text-xs text-mute">{sub}</p> : null}
    </div>
  );
}

export interface StatClusterItem {
  label: string;
  value: ReactNode;
  tone?: MetricTone;
}

/** Page-header stat cluster: right-aligned mono label over a mono 20px value (section 7.4). */
export function StatCluster({ items, className }: { items: StatClusterItem[]; className?: string }) {
  return (
    <div className={cn('flex items-end gap-6', className)}>
      {items.map((s) => (
        <div key={s.label} className="text-right">
          <p className="label-mono">{s.label}</p>
          <p className={cn('mt-0.5 font-mono text-xl tabular-nums leading-none tracking-tight', TONE[s.tone ?? 'ink'])}>{s.value}</p>
        </div>
      ))}
    </div>
  );
}
